import { useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Loader2, Trash2 } from "lucide-react";
import { useGetCompanyMappingsQuery, useCreateCompanyMappingMutation, useDeleteCompanyMappingMutation, useGetCompaniesQuery } from "../../../redux/billing/billing.endpoints";
import { Company } from "src/pages/billing/types/billing.types";

interface CustomMappingRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CustomMappingRulesModal = ({ isOpen, onClose }: CustomMappingRulesModalProps) => {
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [advertiserName, setAdvertiserName] = useState<string>('');

  const { data: companyMappings = [], isLoading: isLoadingMappings } = useGetCompanyMappingsQuery();
  const { data: companies = [] } = useGetCompaniesQuery();
  const [createCompanyMapping, { isLoading: isCreating }] = useCreateCompanyMappingMutation();
  const [deleteCompanyMapping, { isLoading: isDeleting }] = useDeleteCompanyMappingMutation();

  const activeMappings = companyMappings.filter(mapping => mapping.status === 'active');

  const sortedCompanies = companies
    .map(company => ({
      id: company.id,
      name: company.company_name || company.id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const companyLookup = companies.reduce((acc, company) => {
    acc[company.id] = company;
    return acc;
  }, {} as Record<string, Company>);

  const getCompanyName = (companyId: string): string => {
    const company = companyLookup[companyId];
    return company?.company_name || companyId;
  };

  const handleCreateMapping = async() => {
    if (!selectedCompany || !advertiserName.trim()) return;

    try {
      await createCompanyMapping({
        company: selectedCompany,
        advertiser_campaign_name: advertiserName.trim(),
      }).unwrap();

      setSelectedCompany('');
      setAdvertiserName('');
    } catch (error) {
      console.error('Failed to create company mapping:', error);
    }
  };

  const handleDeleteMapping = async(mappingId: string) => {
    try {
      await deleteCompanyMapping(mappingId).unwrap();
    } catch (error) {
      console.error('Failed to delete company mapping:', error);
    }
  };

  const handleClose = () => {
    if (!isCreating && !isDeleting) {
      onClose();
    }
  };

  const canCreate = selectedCompany && advertiserName.trim() && !isCreating;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleClose}
    >
      <DialogContent className="sm:max-w-[800px] bg-white">
        <DialogHeader>
          <DialogTitle>
            Custom Mapping Rules
          </DialogTitle>
          <DialogDescription>
            Manage custom mapping rules for advertisers/campaigns to companies.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="bg-gray-50 p-0 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-1">
              Create New Mapping
            </h3>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500">
                  Advertiser/Campaign Name *
                </label>
                <Input
                  type="text"
                  placeholder="e.g. DrCool"
                  value={advertiserName}
                  onChange={(e) => setAdvertiserName(e.target.value)}
                  disabled={isCreating}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center justify-center h-10">
                <div className="text-gray-600 text-lg font-bold">
                  →
                </div>
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-500">
                  Company *
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  disabled={isCreating}
                  className="w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10"
                >
                  <option value="">
                    Select Company
                  </option>
                  {sortedCompanies.map((company) => (
                    <option
                      key={company.id}
                      value={company.id}
                    >
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Button
                  onClick={handleCreateMapping}
                  disabled={!canCreate}
                  className="bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90 h-10"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add'
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Existing Mappings
            </h3>
            {isLoadingMappings ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
              </div>
            ) : activeMappings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No custom mapping rules found.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-[260px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-gray-600 w-1/3">
                          Advertiser/Campaign
                        </TableHead>
                        <TableHead className="text-gray-600 w-[30px] text-center text-lg font-bold">
                          →
                        </TableHead>
                        <TableHead className="text-gray-600 w-1/3">
                          Company
                        </TableHead>
                        <TableHead className="w-[50px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeMappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell>
                            {mapping.advertiser_campaign_name}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="text-gray-600">
                              →
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {getCompanyName(mapping.company)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="med"
                              onClick={() => handleDeleteMapping(mapping.id)}
                              disabled={isDeleting}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCreating || isDeleting}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomMappingRulesModal;